import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, CalendarDays, Check } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useTenant } from '@/context/TenantContext';
import { useFilters } from '@/context/FilterContext';
import { useAuth } from '@/context/AuthContext';
import { PERIOD_PRESETS, iso, parseLocal } from '@/lib/periods';
import { MultiSelect } from '@/components/layout/MultiSelect';

function fmtFr(isoStr) {
  if (!isoStr) return '';
  const [y, m, d] = String(isoStr).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export const TopFilterBar = () => {
  const navigate = useNavigate();
  const { associations, current, setCurrent, canSwitch, isAll, ALL_CODE, setAllMode } = useTenant();
  const { isAdmin, isCampaignManager } = useAuth();
  const {
    periodKey, setPeriodKey, options, dims, setDim, clearAll, activeCount,
    customRange, setCustomRange,
  } = useFilters();
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draft, setDraft] = useState(undefined); // in-popover preview selection

  const customActive = periodKey === 'custom' && !!customRange?.start;
  const customLabel = customActive
    ? `${fmtFr(customRange.start)} – ${fmtFr(customRange.end)}`
    : 'Plage perso.';

  const dataMin = options?.date_min ? parseLocal(options.date_min) : undefined;
  const dataMax = options?.date_max ? parseLocal(options.date_max) : undefined;

  // When the popover opens, sync the draft with the currently APPLIED range so it
  // never shows a stale selection (e.g. after "Effacer" it starts empty).
  useEffect(() => {
    if (!rangeOpen) return;
    if (periodKey === 'custom' && customRange?.start) {
      setDraft({ from: parseLocal(customRange.start), to: parseLocal(customRange.end) });
    } else {
      setDraft(undefined);
    }
  }, [rangeOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const draftDays = draft?.from
    ? Math.round(((draft.to || draft.from) - draft.from) / 86400000) + 1
    : 0;

  const applyDraft = () => {
    if (!draft?.from) return;
    setCustomRange({ start: iso(draft.from), end: iso(draft.to || draft.from) });
    setPeriodKey('custom');
    setRangeOpen(false);
  };

  const resetDraft = () => {
    setDraft(undefined);
    setCustomRange({ start: null, end: null });
    if (periodKey === 'custom') setPeriodKey('all');
    setRangeOpen(false);
  };

  const defaultMonth = draft?.from
    || (customRange?.start ? parseLocal(customRange.start) : dataMax);

  const onSwitch = (value) => {
    if (value === ALL_CODE) {
      setAllMode(true);
      navigate('/consolidation');
    } else {
      setAllMode(false);
      setCurrent(value);
      navigate('/');
    }
  };

  return (
    <div className="sticky top-0 z-30 glass border-b" data-testid="top-filter-bar">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 sm:px-6">
        {canSwitch && (
          <Select value={isAll ? ALL_CODE : (current || '')} onValueChange={onSwitch}>
            <SelectTrigger className="h-9 w-[210px] rounded-xl bg-card" data-testid="association-switcher">
              <SelectValue placeholder="Association" />
            </SelectTrigger>
            <SelectContent>
              {isAdmin && <SelectItem value={ALL_CODE} data-testid="association-all-option">Toutes les associations</SelectItem>}
              {associations.map((a) => (
                <SelectItem key={a.code} value={a.code} data-testid={`association-option-${a.code}`}>{a.short_name || a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Period presets */}
        <div className="hidden items-center gap-1 rounded-xl border bg-card p-1 lg:flex" data-testid="global-filter-period-presets">
          {PERIOD_PRESETS.map((p) => (
            <button key={p.key} onClick={() => setPeriodKey(p.key)}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${periodKey === p.key ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
              style={periodKey === p.key ? { background: 'var(--tenant-accent-500)' } : {}}
              data-testid={`period-${p.key}`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date range picker */}
        <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
          <PopoverTrigger asChild>
            <button
              className={`hidden items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-xs font-medium transition-colors lg:flex ${customActive ? 'text-white' : 'bg-card text-muted-foreground hover:text-foreground'}`}
              style={customActive ? { background: 'var(--tenant-accent-500)', borderColor: 'var(--tenant-accent-500)' } : {}}
              data-testid="period-custom-trigger">
              <CalendarDays className="h-3.5 w-3.5" />
              {customLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-testid="custom-range-popover">
            <div className="border-b px-3 py-2.5">
              <p className="text-sm font-medium">Plage de dates personnalisée</p>
              <p className="text-xs text-muted-foreground">
                {draft?.from ? fmtFr(iso(draft.from)) : 'Choisissez une date de début'}
                {' → '}
                {draft?.to ? fmtFr(iso(draft.to)) : (draft?.from ? 'date de fin' : '—')}
                {draftDays > 0 && (
                  <span className="ml-2 font-medium accent-text">· {draftDays} j</span>
                )}
              </p>
            </div>
            <Calendar
              mode="range"
              numberOfMonths={2}
              showOutsideDays={false}
              selected={draft}
              onSelect={setDraft}
              defaultMonth={defaultMonth}
              disabled={dataMin && dataMax ? [{ before: dataMin }, { after: dataMax }] : undefined}
              data-testid="custom-range-calendar"
            />
            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-muted-foreground"
                onClick={resetDraft} data-testid="custom-range-reset">
                <X className="h-3.5 w-3.5" /> Réinitialiser
              </Button>
              <Button size="sm" className="h-8 gap-1 rounded-lg text-xs text-white"
                style={{ background: 'var(--tenant-accent-500)' }}
                disabled={!draft?.from}
                onClick={applyDraft} data-testid="custom-range-apply">
                <Check className="h-3.5 w-3.5" /> Appliquer
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Period select (mobile) */}
        <div className="lg:hidden">
          <Select value={customActive ? 'custom' : periodKey} onValueChange={(v) => { if (v !== 'custom') setPeriodKey(v); }}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl bg-card"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIOD_PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              {customActive && <SelectItem value="custom">{customLabel}</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {options && (
            <>
              <MultiSelect label="Code campagne" testid="filter-codes"
                searchable searchPlaceholder="Rechercher un code…"
                options={(options.campaign_codes || []).map((c) => {
                  if (typeof c === 'string') return c;
                  const geo = [c.canal, c.region].filter(Boolean).join(' · ');
                  const sublabel = c.label
                    ? `${c.label}${c.region ? ` · ${c.region}` : ''}`
                    : (geo || null);
                  return {
                    value: c.code,
                    label: c.code,
                    sublabel,
                    keywords: [c.code, c.label, c.canal, c.region].filter(Boolean).join(' '),
                  };
                })}
                selected={dims.campaign_codes} onChange={(v) => setDim('campaign_codes', v)} />
              <div className="hidden md:block">
                <MultiSelect label="Région" testid="filter-regions" options={options.regions || []}
                  selected={dims.regions} onChange={(v) => setDim('regions', v)} />
              </div>
              <div className="hidden lg:block">
                <MultiSelect label="Lieu" testid="filter-lieux"
                  searchable searchPlaceholder="Rechercher un lieu…"
                  options={options.lieux || []}
                  selected={dims.lieux} onChange={(v) => setDim('lieux', v)} />
              </div>
              <div className="hidden xl:block">
                <MultiSelect label="Canal" testid="filter-canaux" options={options.canaux || []}
                  selected={dims.canaux} onChange={(v) => setDim('canaux', v)} />
              </div>
              {!isCampaignManager && (
                <div className="hidden xl:block">
                  <MultiSelect label="Responsable" testid="filter-responsables" options={options.responsables || []}
                    selected={dims.responsables} onChange={(v) => setDim('responsables', v)} />
                </div>
              )}
            </>
          )}
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" className="h-9 gap-1 text-muted-foreground" onClick={clearAll} data-testid="global-filter-clear-all">
              <X className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Effacer</span>
              <Badge variant="secondary" className="ml-1 h-5 rounded-full px-1.5 text-[10px]">{activeCount}</Badge>
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" className="h-9 gap-1.5 rounded-xl text-white" style={{ background: 'var(--tenant-accent-500)' }}
              onClick={() => navigate('/analyses?summary=1')} data-testid="resume-direction-button">
              <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">Résumé Direction</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
